import '../App.css';
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import authgear from "../authgear";
import Header from "../components/Header";

const base_url = process.env.NODE_ENV === "production"
    ? "https://hooks-28xd.onrender.com"
    : "http://localhost:4000";

const EMPTY_FORM = {
    company: "",
    title: "",
    startDate: "",
    endDate: "",
    location: "",
    description: "",
    baseSalary: "",
    compNotes: "",
    isPromotion: false,
    promotionTitle: "",
    promotionDate: "",
    promotionCompNotes: "",
};

const card = {
    background: "#1a1a2e",
    border: "1px solid #2a2a4a",
    borderRadius: "10px",
    padding: "20px",
};

const inputStyle = {
    background: "#0d0d1a",
    border: "1px solid #2a2a4a",
    borderRadius: "6px",
    color: "#ddd",
    padding: "8px 10px",
    fontSize: "13px",
    width: "100%",
    boxSizing: "border-box",
};

const labelStyle = {
    fontSize: "12px",
    color: "#888",
    display: "block",
    marginBottom: "4px",
};

const sectionLabel = {
    fontSize: "11px",
    color: "#555",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "12px",
};

function formatDate(str) {
    if (!str) return "—";
    const [y, m, d] = str.split("-");
    if (!m || !d) return str;
    return `${m}/${d}/${y}`;
}

function formatSalary(n) {
    if (n === undefined || n === null || n === "") return null;
    return `$${Number(n).toLocaleString()}`;
}

const JobHistory = () => {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const getToken = useCallback(async () => {
        await authgear.refreshAccessTokenIfNeeded();
        return authgear.accessToken;
    }, []);

    useEffect(() => {
        if (authgear.sessionState !== "AUTHENTICATED") {
            navigate("/login");
            return;
        }
        fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const fetchJobs = async () => {
        setLoading(true);
        try {
            const token = await getToken();
            const { data } = await axios.get(`${base_url}/jobs`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setJobs(data);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setEditId(null);
        setError(null);
        setShowForm(true);
    };

    const openEdit = (job) => {
        setForm({
            company: job.company || "",
            title: job.title || "",
            startDate: job.startDate || "",
            endDate: job.endDate || "",
            location: job.location || "",
            description: job.description || "",
            baseSalary: job.baseSalary ?? "",
            compNotes: job.compNotes || "",
            isPromotion: !!job.isPromotion,
            promotionTitle: job.promotionTitle || "",
            promotionDate: job.promotionDate || "",
            promotionCompNotes: job.promotionCompNotes || "",
        });
        setEditId(job._id);
        setError(null);
        setShowForm(true);
    };

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const token = await getToken();
            const payload = { ...form };
            payload.baseSalary = payload.baseSalary === "" ? undefined : Number(payload.baseSalary);
            if (!payload.isPromotion) {
                payload.promotionTitle = undefined;
                payload.promotionDate = undefined;
                payload.promotionCompNotes = undefined;
            }
            if (editId) {
                await axios.put(`${base_url}/jobs/${editId}`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                await axios.post(`${base_url}/jobs`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }
            setShowForm(false);
            fetchJobs();
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this job entry?")) return;
        try {
            const token = await getToken();
            await axios.delete(`${base_url}/jobs/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setJobs(prev => prev.filter(j => j._id !== id));
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        }
    };

    return (
        <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#ddd", fontFamily: "Verdana, sans-serif" }}>
            <Header />
            <div style={{ padding: "60px 20px 40px", maxWidth: "860px", margin: "0 auto" }}>

                {/* Header row */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "8px" }}>
                    <h2 style={{ color: "#00d4ff", margin: 0 }}>Job History</h2>
                    {!showForm && (
                        <button
                            onClick={openCreate}
                            style={{
                                padding: "8px 18px",
                                background: "#00d4ff",
                                color: "#0d0d1a",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontWeight: "bold",
                                fontSize: "13px",
                            }}
                        >
                            + Add Job
                        </button>
                    )}
                </div>

                {/* Form */}
                {showForm && (
                    <div style={{ ...card, marginBottom: "24px" }}>
                        <div style={{ ...sectionLabel, marginBottom: "16px" }}>
                            {editId ? "Edit Job" : "New Job"}
                        </div>
                        <form onSubmit={handleSave}>
                            <div style={{ display: "flex", gap: "12px", marginBottom: "14px", flexWrap: "wrap" }}>
                                <div style={{ flex: 1, minWidth: "200px" }}>
                                    <label style={labelStyle}>Company</label>
                                    <input
                                        style={inputStyle}
                                        value={form.company}
                                        onChange={e => handleChange("company", e.target.value)}
                                        placeholder="e.g. Acme Corp"
                                        required
                                    />
                                </div>
                                <div style={{ flex: 1, minWidth: "200px" }}>
                                    <label style={labelStyle}>Title</label>
                                    <input
                                        style={inputStyle}
                                        value={form.title}
                                        onChange={e => handleChange("title", e.target.value)}
                                        placeholder="e.g. Software Engineer"
                                        required
                                    />
                                </div>
                            </div>

                            <div style={{ display: "flex", gap: "12px", marginBottom: "14px", flexWrap: "wrap" }}>
                                <div style={{ flex: 1, minWidth: "160px" }}>
                                    <label style={labelStyle}>Start date</label>
                                    <input
                                        type="date"
                                        style={inputStyle}
                                        value={form.startDate}
                                        onChange={e => handleChange("startDate", e.target.value)}
                                        required
                                    />
                                </div>
                                <div style={{ flex: 1, minWidth: "160px" }}>
                                    <label style={labelStyle}>End date (blank = current)</label>
                                    <input
                                        type="date"
                                        style={inputStyle}
                                        value={form.endDate}
                                        onChange={e => handleChange("endDate", e.target.value)}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: "14px" }}>
                                <label style={labelStyle}>Location</label>
                                <input
                                    style={inputStyle}
                                    value={form.location}
                                    onChange={e => handleChange("location", e.target.value)}
                                    placeholder="e.g. Remote / Denver, CO"
                                />
                            </div>

                            <div style={{ marginBottom: "16px" }}>
                                <label style={labelStyle}>Description</label>
                                <textarea
                                    rows={3}
                                    style={{ ...inputStyle, resize: "vertical" }}
                                    value={form.description}
                                    onChange={e => handleChange("description", e.target.value)}
                                    placeholder="Optional notes about the role"
                                />
                            </div>

                            <div style={{ ...sectionLabel, marginBottom: "12px" }}>Compensation</div>
                            <div style={{ display: "flex", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
                                <div style={{ flex: 1, minWidth: "160px" }}>
                                    <label style={labelStyle}>Base salary</label>
                                    <input
                                        type="number"
                                        style={inputStyle}
                                        value={form.baseSalary}
                                        onChange={e => handleChange("baseSalary", e.target.value)}
                                        placeholder="e.g. 120000"
                                    />
                                </div>
                                <div style={{ flex: 2, minWidth: "220px" }}>
                                    <label style={labelStyle}>Bonus / equity / other notes</label>
                                    <input
                                        style={inputStyle}
                                        value={form.compNotes}
                                        onChange={e => handleChange("compNotes", e.target.value)}
                                        placeholder="e.g. 10% bonus target, 500 RSUs"
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: "16px" }}>
                                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px", color: "#ddd" }}>
                                    <input
                                        type="checkbox"
                                        checked={form.isPromotion}
                                        onChange={e => handleChange("isPromotion", e.target.checked)}
                                    />
                                    This role included a promotion
                                </label>
                            </div>

                            {form.isPromotion && (
                                <div style={{ ...card, background: "#0d0d1a", marginBottom: "16px" }}>
                                    <div style={{ ...sectionLabel, marginBottom: "12px", color: "#a855f7" }}>Promotion Details</div>
                                    <div style={{ display: "flex", gap: "12px", marginBottom: "14px", flexWrap: "wrap" }}>
                                        <div style={{ flex: 1, minWidth: "160px" }}>
                                            <label style={labelStyle}>New title</label>
                                            <input
                                                style={inputStyle}
                                                value={form.promotionTitle}
                                                onChange={e => handleChange("promotionTitle", e.target.value)}
                                                placeholder="e.g. Senior Software Engineer"
                                            />
                                        </div>
                                        <div style={{ flex: 1, minWidth: "160px" }}>
                                            <label style={labelStyle}>Promotion date</label>
                                            <input
                                                type="date"
                                                style={inputStyle}
                                                value={form.promotionDate}
                                                onChange={e => handleChange("promotionDate", e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label style={labelStyle}>New compensation notes</label>
                                        <input
                                            style={inputStyle}
                                            value={form.promotionCompNotes}
                                            onChange={e => handleChange("promotionCompNotes", e.target.value)}
                                            placeholder="e.g. base raised to 140000, refresh grant"
                                        />
                                    </div>
                                </div>
                            )}

                            {error && (
                                <div style={{ color: "#f87171", fontSize: "13px", marginBottom: "12px" }}>{error}</div>
                            )}

                            <div style={{ display: "flex", gap: "10px" }}>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    style={{
                                        padding: "8px 20px",
                                        background: saving ? "#2a2a4a" : "#00d4ff",
                                        color: saving ? "#555" : "#0d0d1a",
                                        border: "none",
                                        borderRadius: "6px",
                                        cursor: saving ? "not-allowed" : "pointer",
                                        fontWeight: "bold",
                                        fontSize: "13px",
                                    }}
                                >
                                    {saving ? "Saving..." : editId ? "Save Changes" : "Create Job"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setShowForm(false); setEditId(null); setError(null); }}
                                    style={{
                                        padding: "8px 16px",
                                        background: "transparent",
                                        color: "#555",
                                        border: "1px solid #2a2a4a",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Job list */}
                {loading ? (
                    <div style={{ color: "#555", fontSize: "13px" }}>Loading...</div>
                ) : jobs.length === 0 ? (
                    <div style={{ color: "#555", fontSize: "13px" }}>No jobs yet. Add one above.</div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        {jobs.map(job => (
                            <div key={job._id} style={card}>
                                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                                    <div style={{ flex: 1, minWidth: "200px" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                            <span style={{ fontSize: "15px", fontWeight: "bold", color: "#ddd" }}>{job.title}</span>
                                            <span style={{ fontSize: "13px", color: "#888" }}>@ {job.company}</span>
                                            {job.isPromotion && (
                                                <span style={{
                                                    fontSize: "10px",
                                                    padding: "2px 8px",
                                                    borderRadius: "4px",
                                                    border: "1px solid #a855f7",
                                                    color: "#a855f7",
                                                }}>
                                                    Promoted
                                                </span>
                                            )}
                                        </div>

                                        <div style={{ fontSize: "12px", color: "#555", marginTop: "6px" }}>
                                            {formatDate(job.startDate)} → {job.endDate ? formatDate(job.endDate) : "Present"}
                                            {job.location && ` · ${job.location}`}
                                        </div>

                                        {(formatSalary(job.baseSalary) || job.compNotes) && (
                                            <div style={{ fontSize: "12px", color: "#aaa", marginTop: "6px" }}>
                                                {[formatSalary(job.baseSalary), job.compNotes].filter(Boolean).join(" · ")}
                                            </div>
                                        )}

                                        {job.description && (
                                            <div style={{ fontSize: "12px", color: "#666", marginTop: "6px", fontStyle: "italic" }}>
                                                {job.description}
                                            </div>
                                        )}

                                        {job.isPromotion && (job.promotionTitle || job.promotionDate || job.promotionCompNotes) && (
                                            <div style={{
                                                marginTop: "10px",
                                                paddingTop: "10px",
                                                borderTop: "1px solid #2a2a4a",
                                                fontSize: "12px",
                                                color: "#a855f7",
                                            }}>
                                                Promoted{job.promotionTitle && ` to ${job.promotionTitle}`}
                                                {job.promotionDate && ` (${formatDate(job.promotionDate)})`}
                                                {job.promotionCompNotes && <div style={{ color: "#aaa", marginTop: "4px" }}>{job.promotionCompNotes}</div>}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                                        <button
                                            onClick={() => openEdit(job)}
                                            style={{
                                                padding: "5px 12px",
                                                background: "transparent",
                                                color: "#00d4ff",
                                                border: "1px solid #00d4ff",
                                                borderRadius: "6px",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                            }}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(job._id)}
                                            style={{
                                                padding: "5px 12px",
                                                background: "transparent",
                                                color: "#f87171",
                                                border: "1px solid #4a2a2a",
                                                borderRadius: "6px",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                            }}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default JobHistory;
